import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import axios from 'axios';
import Dropzone from 'react-dropzone';
import { Formik, Form } from 'formik';
import * as Cookies from 'js-cookie';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';
import * as tokenService from '@nexxt/common/firebase/auth/userDetails';
import { getQueryString } from '@nexxt/common/services/TextService';
import AuthContext from '../../../contexts/AuthContext';
import TextField from '../../common/textField';
import {
    getProfileValidationSchema,
    passwordValidationSchema
} from '../../common/validation/validationSchema';
import * as profileService from '../../../services/profile.service';
import { User } from '../../../interface/user.interface';
import Spinner from '../../common/Loader/Spinner';
import NotifyHOC from '../../../HOC/Notifications';
import Loader from '../../common/Loader/Loader';
import {
    logoutRequest,
    setUserDetails
} from '../../../actions/authentication.actions';
import { alertMessage } from '../../../constants/alertMessage';
import { push } from 'connected-react-router';
import * as Messages from '../../../mapping/messages';
import { Notes } from '../../common/notes';
import { ALERT_MESSAGE_TYPES } from '../../../interface/project.interface';
interface MyProfileProps {
    user: User;
    toastNotification: any;
    setUserDetails: (user: any) => void;
    logoutRequest: () => void;
    changePath: (path: string) => void;
}

const acceptFormats = {
    'image/*': []
};

const MyProfile = (props: MyProfileProps) => {
    const [isRequesting, setIsRequesting] = React.useState(false);
    const [accepted, setAccepted] = React.useState('');
    const [rejected, setRejected] = React.useState('');
    const [uploading, setUploading] = React.useState(false);
    const [isExpired, setIsExpired] = React.useState(false);
    const [profilePictureURL, setProfilePictureURL] = React.useState('');
    const [localUserDetails, setLocalUserDetails] = React.useState(null);
    const [defaultProfilePicture, setDefaultProfilePicture] = React.useState(
        `${STATIC_STORAGE_URL}/usr/dc2a9fef-f28a-49d1-8ce4-61789ca8e51a_2b7cd9bb-19d9-4040-9b2f-08d71b34536b_download.png`
    );
    const [isResetPassword, setIsResetPassword] = React.useState(false);
    const { firebaseAuth } = React.useContext(AuthContext);
    const userDetails = tokenService.getUserDetails();

    React.useEffect(() => {
        setLocalUserDetails(props.user);
        setProfilePictureURL(props.user?.profilePicture);
        const expired = getQueryString('exp') === '1';
        setIsExpired(expired);
        if (expired) {
            props.toastNotification.warning(
                'Your password has expired. Please change the password.'
            );
        }
    }, []);

    const handleSubmit = async (values: any) => {
        updateProfile(values);
    };

    const updateProfile = async (values) => {
        setIsRequesting(true);
        const { lastName, firstName } = values;

        try {
            await profileService.updateProfile(props.user.id, {
                lastName,
                firstName,
                profilePictureURL: profilePictureURL
                    ? profilePictureURL
                    : defaultProfilePicture
            });

            const user = {
                lastName,
                firstName,
                profilePicture: profilePictureURL
            };

            tokenService.setUserDetails({ lastName, firstName, user });

            props.setUserDetails({ localUserDetails, ...user });

            setIsRequesting(false);

            window.location.reload();
        } catch (error) {
            setIsRequesting(false);
            props.toastNotification.error(error.response.data.message);
        }
    };

    const updatePassword = async (password) => {
        try {
            const cssToken = Cookies.get('csst');
            setIsRequesting(true);

            await firebaseAuth.currentUser.updatePassword(password);
            await axios.post(
                `${process.env.INCA_SERVER_URL}/auth/update-expiry`,
                {},
                {
                    headers: { Authorization: `Bearer ${cssToken}` },
                    withCredentials: true
                }
            );
            setIsRequesting(false);
            setIsResetPassword(false);
            tokenService.setUserDetails({
                ...userDetails,
                passwordExpired: false
            });
            props.toastNotification.success(
                alertMessage.SUCCESS_UPDATE_PASSWORD
            );

            if (isExpired) {
                window.location.href = location.pathname;
            }
        } catch (error) {
            setIsRequesting(false);
            const errorMessage = error.response.data.message
                ? error.response.data.message
                : 'Failed to change password!';
            props.toastNotification.error(errorMessage);
        }
    };

    const resetPassword = () => {
        setIsResetPassword((prevIsResetPassword) => !prevIsResetPassword);
    };

    const upload = (uploadAccepted, uploadRejected) => {
        console.log('uploadAccepted:', uploadAccepted);
        console.log('accepted:', accepted);
        if (uploadAccepted.length > 0) {
            setAccepted(uploadAccepted);
            setRejected(uploadRejected);
            setUploading(true);
            uploadImage(uploadAccepted[0]);
        }
    };

    const uploadImage = async (imageBlob) => {
        console.log('accepted:', accepted);
        if (imageBlob) {
            const formData = new FormData();
            formData.append('file', imageBlob);

            const response = await profileService.uploadImage(formData);
            setProfilePictureURL(response.data.full);
            setUploading(false);
        }
    };

    const removeImage = () => {
        setProfilePictureURL(null);
    };

    const signOut = async () => {
        try {
            await axios.post(
                `${process.env.INCA_SERVER_URL}/auth/signout`,
                {},
                { withCredentials: true }
            );
            await firebaseAuth.signOut();
            props.toastNotification.success(Messages.LOGOUT_MESSAGE);
            localStorage.clear();
            window.location.href = process.env.AUTH_URL;
        } catch (e) {
            props.toastNotification.error(Messages.LOGOUT_MESSAGE_ERROR);
            console.log(e);
        }
    };

    const { user } = props;

    if (isRequesting) {
        return <Loader />;
    }

    return (
        <div className="card-container">
            {userDetails.id ? (
                <div className="card-wrapper bg-white">
                    <div className="card-wrapper--header">
                        <div className="card-wrapper--profile-img">
                            {profilePictureURL && (
                                <span
                                    className="card-wrapper--remove-file"
                                    onClick={removeImage}
                                >
                                    <i className="nx nx-icon nx-close" />
                                </span>
                            )}

                            <div className="card-wrapper--image-hold">
                                <img
                                    src={
                                        profilePictureURL ||
                                        defaultProfilePicture
                                    }
                                />
                                {uploading && <Spinner size="spinner-small" />}
                            </div>
                        </div>

                        <Dropzone
                            accept={acceptFormats}
                            multiple={false}
                            onDrop={upload}
                        >
                            {({ getRootProps, getInputProps }: any) => (
                                <div className="dropzone" {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <div className="btn btn--lt-gray btn--sm">
                                        Change Photo
                                    </div>
                                </div>
                            )}
                        </Dropzone>
                    </div>
                    <div className="card-wrapper--body">
                        <Formik
                            initialValues={{
                                email: userDetails?.email,
                                password: '',
                                lastName: userDetails?.lastName,
                                firstName: userDetails?.firstName
                            }}
                            validationSchema={getProfileValidationSchema}
                            onSubmit={(values, actions) => {
                                handleSubmit(values);
                            }}
                            render={({
                                values,
                                touched,
                                handleChange,
                                handleBlur,
                                errors,
                                dirty,
                                isSubmitting
                            }) => (
                                <Form>
                                    <TextField
                                        type="text"
                                        name="firstName"
                                        value={values.firstName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="First name"
                                        label="First Name"
                                        errorMessage={errors.firstName}
                                        touchedValue={touched.firstName}
                                    />
                                    <TextField
                                        type="text"
                                        name="lastName"
                                        value={values.lastName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Last name"
                                        label="Last name"
                                        errorMessage={errors.lastName}
                                        touchedValue={touched.lastName}
                                    />
                                    <TextField
                                        type="text"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Email"
                                        label="Email"
                                        errorMessage={errors.email}
                                        touchedValue={touched.email}
                                        disabled={true}
                                    />

                                    {/* <TextField
                                        type="password"
                                        name="password"
                                        value={values.password}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Password"
                                        label="Password"
                                        errorMessage={errors.password}
                                        touchedValue={touched.password}
                                    /> */}

                                    {/* <button
                                        disabled={
                                            isSubmitting ||
                                            // !isEmpty(errors) ||
                                            // requestingSession ||
                                            !dirty
                                        }
                                        type="submit"
                                        className="btn btn--fluid btn--teal btn--lg"
                                    >
                                        Login
                                    </button> */}
                                    {/* <button
                                        className="btn btn--sm btn--blue"
                                        onClick={() =>
                                            updatePassword(values.password)
                                        }
                                    >
                                        Reset Password
                                    </button> */}
                                    <div className="btn-wrapper">
                                        <button
                                            className="btn btn--sm btn--blue"
                                            type="submit"
                                        >
                                            Save profile
                                        </button>
                                    </div>
                                </Form>
                            )}
                        />
                        <div className="reset-block">
                            {isExpired ? (
                                <div style={{ margin: '10px' }}>
                                    <Notes
                                        type={ALERT_MESSAGE_TYPES.DANGER}
                                        note={
                                            'Your password was expired. Please reset your password.'
                                        }
                                    />
                                </div>
                            ) : null}
                            {isResetPassword ? (
                                <Formik
                                    initialValues={{
                                        password: '',
                                        confirmPassword: ''
                                    }}
                                    validationSchema={passwordValidationSchema}
                                    onSubmit={(values, actions) => {
                                        updatePassword(values.password);
                                    }}
                                    render={({
                                        values,
                                        touched,
                                        handleChange,
                                        handleBlur,
                                        errors
                                    }) => (
                                        <Form>
                                            <TextField
                                                type="password"
                                                name="password"
                                                value={values.password}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Password"
                                                label="Password"
                                                errorMessage={errors.password}
                                                touchedValue={touched.password}
                                            />

                                            <TextField
                                                type="password"
                                                name="confirmPassword"
                                                value={values.confirmPassword}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Confirm Password"
                                                label="Confirm Password"
                                                errorMessage={
                                                    errors.confirmPassword
                                                }
                                                touchedValue={
                                                    touched.confirmPassword
                                                }
                                            />
                                            <div className="btn-wrapper">
                                                <button
                                                    className="btn btn--sm btn--blue"
                                                    // onClick={() =>
                                                    //     updatePassword(values.password)
                                                    // }
                                                    type="submit"
                                                >
                                                    Change
                                                </button>
                                                <button
                                                    className="btn btn--sm btn--lt-gray"
                                                    // onClick={() =>
                                                    //     updatePassword(values.password)
                                                    // }

                                                    onClick={() =>
                                                        resetPassword()
                                                    }
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </Form>
                                    )}
                                />
                            ) : (
                                <React.Fragment>
                                    <TextField
                                        type="password"
                                        name="password"
                                        placeholder="*******"
                                        label="Password"
                                        disabled={true}
                                    />
                                    <div className="btn-wrapper">
                                        <button
                                            className="btn btn--sm btn--blue"
                                            onClick={() => resetPassword()}
                                        >
                                            Change Password
                                        </button>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                        {/* <Formik
                                initialValues={{
                                    password: "",
                                }}
                                validationSchema={passwordValidationSchema}
                                onSubmit={(values, actions) => {
                                    updatePassword(values.password);
                                }}
                                render={({
                                    values,
                                    touched,
                                    handleChange,
                                    handleBlur,
                                    errors,
                                    dirty,
                                    isSubmitting,
                                }) => (
                                    <Form>
                                        <TextField
                                            type="password"
                                            name="password"
                                            value={values.password}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Password"
                                            label="Password"
                                            errorMessage={errors.password}
                                            touchedValue={touched.password}
                                        />

                                        <button
                                            className="btn btn--sm btn--blue"
                                            // onClick={() =>
                                            //     updatePassword(values.password)
                                            // }
                                            type="submit"
                                        >
                                            Reset Password
                                        </button>
                                    </Form>
                                )}
                            /> */}
                        {/* <form className="form">
                            <div className="form-group">
                                <label className="form-group__label form-group__label--block">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    className="form-group__control disabled"
                                />
                            </div>
                            <div className="form-group">
                                <button
                                    className="btn btn--sm btn--blue"
                                >
                                    Reset Password
                                </button>
                            </div>
                        </form> */}
                    </div>
                </div>
            ) : null}
            <button
                className="btn btn--full btn--blue"
                style={{ marginTop: '10px' }}
                onClick={signOut}
            >
                Log out
            </button>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    setUserDetails: (user) => {
        dispatch(setUserDetails(user));
    },
    logoutRequest: () => dispatch(logoutRequest()),
    changePath: (path) => {
        dispatch(push(path));
    }
});

const mapStateToProps = (store) => {
    const {
        utilsReducer: { showDropdown },
        authReducer: { user, userDetails }
    } = store;
    return {
        showDropdown,
        user,
        userDetails
    };
};

export default NotifyHOC(
    connect(mapStateToProps, mapDispatchToProps)(MyProfile)
);
